import EncodingsCQ from 'api/EncodingsCQ'
import { Causal, CQCounters, CQLot, CQSerie, CQTag, TestingSheet } from 'api/types'
import AppStore from 'AppStore'
import Sounds from 'shared/Sounds'
import { askUserConfirmation, showToast } from 'shared/utils'
import { __, T } from 'translations/i18n'
import QualityControlAgent from './QualityControlAgentNew'
import DataDogLogger from '../../shared/DataDogLogger'
import QualityProviderNew from './QualityProviderNew'
import styled from '@emotion/styled'
import { Box } from '../../components'

export default class QualityProvider {
  static async commonComponentDidMount(
    stopReader: () => Promise<void>,
    isPelletteria: boolean,
    setState: (causal: Causal[]) => void
  ) {
    try {
      await stopReader()
      const causal = await EncodingsCQ.fetchCausal(isPelletteria ? 'P' : 'C')
      setState(causal)
    } catch (error) {
      DataDogLogger.addError(error, { type: 'fetchCausal' })
      showToast({
        sound: false,
        title: __(T.error.error),
        description: (error as Error).message,
        status: 'error',
      })
    }
  }

  static startReader = async (
    setStateIsReading: (isReading: boolean) => void,
    setStateTag: (tag: { uid: any; status: any; errorDesc: any; uuid: any }) => void,
    verifyReadedTag: (tag: { uid: any; status: any; errorDesc: any; uuid: any }) => Promise<any>,
    options: { value: string; label: string; active: boolean }[],
    selectedCausal?: Causal
  ): Promise<void> => {
    try {
      setStateIsReading(true)
      if (!AppStore.emulation) {
        await QualityControlAgent.testConnection()
        const mode = options.find((opt) => opt.active)?.value
        if (mode === 'SCARTO' && !selectedCausal) throw new Error(__(T.error.select_causal_before_proceeding))
        const tag = await QualityControlAgent.readTag('fake', 'fake')
        if (tag.status === '4')
          return QualityProvider.startReader(setStateIsReading, setStateTag, verifyReadedTag, options, selectedCausal) // Richiamo corretto
        if (tag.status === '99') return // Operazione interrotta senza popup di errore
        if (tag.status === '0' && tag.uid === '') throw new Error(__(T.error.reading_error)) // Lettura andata a buon fine ma senza il tag
        if (tag.status !== '0') throw new Error(tag.errorDesc) // Gestione di tutti gli altri tipi di errore
        setStateTag(tag)
        await verifyReadedTag(tag)
      }
    } catch (error) {
      setStateIsReading(false)
      showToast({
        title: __(T.error.error),
        description: (error as Error).message ?? __(T.error.WEBRESOURCE_GENERIC_ERROR),
        status: 'error',
      })
    }
  }

  static async searchSerie(
    isPelletteria: boolean,
    code: string,
    serie: any,
    operation: any,
    closeSerie: any,
    searchSerie: any,
    showSelectProductModal: any,
    changeStatusSerie: any
  ) {
    if (isPelletteria && code.length !== 10) throw new Error(__(T.error.serie_code_10_characters))
    if (!isPelletteria && code.length !== 14 && code.length !== 17)
      throw new Error(__(T.error.serie_code_14_characters))
    const data = await EncodingsCQ.getCustomProductionOrder(code, operation)

    if (!data) throw new Error(__(T.error.serie_not_found))

    //controllo se la serie appartiene al place dell'utente
    if (data.worker.code !== AppStore.defaultPlace?.code) throw new Error(__(T.error.serie_wrong_place))

    if (!AppStore.isInspector && data.status === 'CLOSED') throw new Error(__(T.error.serie_inspector_check))

    //se la nuova serie (10 caratteri non corrisponde a quella corrente allora va chiusa quella corrente e aperta quella nuova)
    if (serie && data.code !== serie?.code) {
      if (
        await askUserConfirmation(
          __(T.messages.serie_change_confirm),
          __(T.messages.serie_close_product_not_associated)
        )
      ) {
        if (!(await closeSerie())) return
        searchSerie(code)
        return
      }
      return
    }

    let row

    if (code.length === 10 && data.rows.length > 1) row = await showSelectProductModal(data.rows)

    if (!row) {
      if (isPelletteria) row = data.rows[0]
      else {
        row = data.rows.find((r) => r.rowCode === code) as any
        // fallback per codici (vecchi) serie 14 caratteri
        if (!row && code.length === 14) {
          const barcodeTo17chars = `${code.slice(0, 4)}000${code.slice(4)}`
          row = data.rows.find((r) => r.rowCode === barcodeTo17chars) as any
        }
      }
    }

    if (!row.product) throw Error(__(T.error.serie_not_found))

    //quando l'ispettore apre la serie allora la metto in stato inspected_2
    if (AppStore.isInspector) await changeStatusSerie('INSPECTED_2', 'ispezione', data.id)

    const newSerie = {
      id: data.id,
      code: data.code,
      row: row,
      product: row.product,
      worker: data.worker,
      customerOrder: data.customerOrder,
      productId: row.product.id,
      nationCode: data.nationCode,
      destination: data.destination,
      qualityCode: data.qualityCode,
      data: {
        values: {
          'Tag/Cal': `${row.product.size.value}/${row.product.fit.value}`,
          Modello: row.product.style.value,
          Variante: row.product.variant.value,
          Stagione: row.product.season.slice(0, 2) + row.product.season.slice(4),
          Lavorante: data.worker.description ?? 'aa',
        },
        descriptions: {
          Taglia: row.product.size.valueDescription,
          Calzata: row.product.fit.valueDescription,
          Modello: row.product.style.valueDescription,
          Variante: row.product.variant.valueDescription,
        },
      },
      counters: {
        totalPieces: data.availableQuantity,
        tested: data.tested,
        discardedPieces: data.discarded,
        inspectorTested: data.inspectorTested,
        toBeTested: data.toBeTested > data.tested ? data.tested : data.toBeTested,
        availableQuantity: data.availableQuantity,
      },
    }
    Sounds.tap()
    return newSerie
  }

  static async verify(
    tag: CQTag,
    options: any,
    serie: any,
    operation: any,
    setError: any,
    setErrorForceCollaudo: any,
    Associate: any,
    Drop: any,
    Delete: any,
    setItem: any,
    forceCollaudo: any,
    forceDrop: any,
    stopReader: any
  ) {
    const mode = options.find((opt) => opt.active === true).value

    const res = await EncodingsCQ.verify({
      configurationId: operation.id,
      identifiers: [{ code: tag.uid, type: 'NFCTag' }] as any,
      productionOrderId: serie.id,
      productId: serie.productId,
    })

    if (res.errors.find((error) => error.errorCode === 'FERRAGAMO.ERROR.TAG_STATUS_ERROR')) {
      setError(__(T.error.status_not_admitted))
      return
    }

    if (!res.item) {
      if (AppStore.isInspector) {
        setError(__(T.error.not_collaudated_product))
        return
      }
      if (mode === 'collaudo') {
        await stopReader(false, true)
        await Associate()
        return
      }
      if (mode === 'scarto') {
        await stopReader()
        await Drop()
        return
      }
      if (mode === 'cancellazione') {
        setError(__(T.error.not_collaudated_product))
        return
      }
    }

    const item = await EncodingsCQ.fetchItemById(res.item.id)

    if (!res.success && res.errors.find((error) => error.errorCode === 'FERRAGAMO.ERROR.SERIES_MISMATCH_ERROR')) {
      setError(__(T.error.not_associated_product))
      return
    }

    if (mode === 'collaudo') {
      if (AppStore.isInspector) {
        if (
          AppStore.isInspector &&
          !res.success &&
          res.errors.length === 1 &&
          res.errors[0].errorCode === 'FERRAGAMO.ERROR.TAG_STATUS_ERROR_CQ2'
        ) {
          setError(__(T.error.product_already_associated))
        } else if (
          (AppStore.isInspector &&
            !res.success &&
            res.errors.length === 1 &&
            res.errors[0].errorCode === 'FERRAGAMO.ERROR.TAG_STATUS_ERROR_SC') ||
          res.errors[0].errorCode === 'FERRAGAMO.ERROR.TAG_STATUS_ERROR_SCNR'
        ) {
          setError(__(T.error.product_already_dropped))
        } else if (
          res.success ||
          (AppStore.isInspector &&
            !res.success &&
            res.errors.length === 1 &&
            res.errors[0].errorCode === 'FERRAGAMO.ERROR.TAG_STATUS_ERROR_CQ')
        ) {
          await stopReader(true)
          setItem(item)
          await forceCollaudo('CQ2')
        }
      } else if (
        !AppStore.isInspector &&
        !res.success &&
        res.errors.length === 1 &&
        res.errors.find((err) => err.errorCode === 'FERRAGAMO.ERROR.TAG_STATUS_ERROR_SCNR')
      ) {
        setError(__(T.error.product_irreversible_dropped))
      } else if (
        !AppStore.isInspector &&
        !res.success &&
        res.errors.length === 1 &&
        res.errors[0].errorCode === 'FERRAGAMO.ERROR.TAG_STATUS_ERROR_CQ'
      ) {
        setError(__(T.error.product_already_associated))
      } else if (
        !AppStore.isInspector &&
        !res.success &&
        res.errors.length === 1 &&
        res.errors[0].errorCode === 'FERRAGAMO.ERROR.TAG_STATUS_ERROR_CQ2'
      ) {
        setError(__(T.error.product_already_associated_by_inspector))
      } else if (res.success) {
        await stopReader()
        setItem(item)
        await Associate()
      } else {
        await stopReader(true)
        setErrorForceCollaudo(item)
      }
    } else if (mode === 'scarto') {
      if (AppStore.isInspector) {
        if (
          !res.success &&
          res.errors.find(
            (error) =>
              error.errorCode === 'FERRAGAMO.ERROR.TAG_STATUS_ERROR_CQ' ||
              error.errorCode === 'FERRAGAMO.ERROR.TAG_STATUS_ERROR_CQ2'
          )
        ) {
          await stopReader()
          forceDrop(__(T.messages.drop_proceed))
          return
        }
        if (
          AppStore.isInspector &&
          !res.success &&
          res.errors.find(
            (error) =>
              error.errorCode === 'FERRAGAMO.ERROR.TAG_STATUS_ERROR_SC' ||
              error.errorCode === 'FERRAGAMO.ERROR.TAG_STATUS_ERROR_SCNR'
          )
        ) {
          await stopReader()
          forceDrop(__(T.messages.drop_proceed))
          return
        }
      } else if (
        !AppStore.isInspector &&
        !res.success &&
        res.errors.find((error) => error.errorCode === 'FERRAGAMO.ERROR.TAG_STATUS_ERROR_SC')
      ) {
        setError(__(T.error.product_already_dropped))
        return
      }
      if (
        !AppStore.isInspector &&
        !res.success &&
        res.errors.find((err) => err.errorCode === 'FERRAGAMO.ERROR.TAG_STATUS_ERROR_SCNR')
      ) {
        setError(__(T.error.product_irreversible_dropped))
      } else {
        await stopReader()
        await Drop()
      }
    } else if (mode === 'cancellazione') {
      if (
        !AppStore.isInspector &&
        !res.success &&
        res.errors.find((err) => err.errorCode === 'FERRAGAMO.ERROR.TAG_STATUS_ERROR_SCNR')
      ) {
        setError(__(T.error.product_irreversible_dropped))
      } else if (res.success && !res.item) {
        const res = await QualityControlAgent.cancelTag(
          AppStore.loggedUser?.username as string,
          tag.uid,
          AppStore.defaultPostazione ?? ''
        )
        if (res?.status !== '0') {
          throw Error(__(T.error.tag_reset_error))
        }
      } else {
        await stopReader()
        setItem(item)
        await Delete()
      }
    }
  }

  static async searchQualitySheet(qualityCode?: string, workerCode?: string, productStyle?: string) {
    if (!qualityCode || !workerCode || !productStyle) return undefined
    return await EncodingsCQ.getQualitySheet(qualityCode, workerCode, productStyle)
  }

  static async searchTestingSheet(serieId: string) {
    if (!serieId) return undefined
    return await EncodingsCQ.getTestingSheet(serieId)
  }

  static async createTestingSheet(testingSheet: Partial<TestingSheet>) {
    return await EncodingsCQ.createTestingSheet(testingSheet)
  }

  static async updateTestingSheet(testingSheet: Partial<TestingSheet>) {
    return await EncodingsCQ.updateTestingSheet(testingSheet)
  }

  static async addFilesQualitySheet(testingSheetCode: string, files: any) {
    return await EncodingsCQ.addFilesQualitySheet(testingSheetCode, files)
  }

  static getEncodati(lotCode?: string, counters?: CQCounters) {
    if (!counters) return 0
    let encodati = 0
    counters.itemStatusCounts.lotCounts
      .filter((lot) => lot.lotCode !== lotCode)
      .forEach((lot) => {
        encodati += lot.tested ? lot.tested : 0
      })
    return encodati
  }

  static async checkStatusCancella(status: string) {
    let message: string
    switch (status) {
      case 'CQ':
        message = __(T.misc.warning_item_just_encoded)
        break
      case 'CQ2':
        message = __(T.misc.warning_item_just_tested)
        break
      case 'SC':
        message = __(T.misc.warning_item_just_discarded)
        break
      case 'SCNR':
        message = __(T.misc.warning_item_just_discarded_nr)
        break
      default:
        message = ''
        break
    }
    return await askUserConfirmation(message, __(T.misc.proceed))
  }

  static async collaudaItemEmulazione(
    additionalSerieData:
      | {
          workerSyncCode: string
          productSku: string
          productSize: string
          productCode: string
          customerOrder: string
          nationCode: string
        }
      | undefined,
    tag?: CQTag,
    serie?: CQSerie
  ) {
    if (!AppStore.emulation) {
      const cliente = additionalSerieData?.customerOrder?.trim() || 'N00035'
      //if the string is less than 6 characters it will add 0 at the end of the string
      const clienteFinal = cliente.length < 6 ? cliente.padEnd(6, '0') : cliente
      const res = await QualityControlAgent.writeTagCQ(
        AppStore.loggedUser?.username as string,
        AppStore.defaultPostazione ?? '',
        tag?.uid as string,
        additionalSerieData?.workerSyncCode as string,
        serie?.code as string,
        (serie?.data.values.Stagione as string) ?? '-',
        (additionalSerieData?.productSku as string) ?? '',
        additionalSerieData?.productSize ?? '',
        clienteFinal,
        additionalSerieData?.nationCode ?? ''
      )
      if (res?.status !== '0') throw Error(__(T.error.writing_tag_error))
    }
  }

  static async cancellaItemEmulazione(
    setState: (isWriting: boolean) => void,
    tag?: CQTag,
    serie?: CQSerie,
    item?: any
  ) {
    DataDogLogger.addAction('processDelete', { tag, serie, item })
    setState(true)

    if (!AppStore.emulation) {
      const res = await QualityControlAgent.cancelTag(
        AppStore.loggedUser?.username as string,
        tag?.uid as string,
        AppStore.defaultPostazione ?? ''
      )
      if (res?.status !== '0') {
        throw Error(__(T.error.tag_reset_error))
      }
    }
  }

  static async cancellaItem(
    setState: (errorSerie: any, isWriting: boolean, isReading: boolean) => void,
    changeOptions: (option: string) => void,
    getUpdatedSerie: (lastCode: string) => Promise<any>,
    lastSearchedSerieCode?: string
  ) {
    try {
      setState(undefined, false, false)
      Sounds.success()
      showToast({
        title: __(T.misc.success),
        description: __(T.messages.product_cancel_correctly),
        status: 'success',
      })
      changeOptions('COLLAUDO')
      await getUpdatedSerie(lastSearchedSerieCode ?? '')
    } catch (error) {
      showToast({
        title: __(T.error.error),
        description: (error as Error).message ?? __(T.error.WEBRESOURCE_GENERIC_ERROR),
        status: 'error',
      })
    }
  }

  static showLotsModal(lots?: CQLot[], serie?: CQSerie, counters?: CQCounters) {
    const lotCounts = counters?.itemStatusCounts.lotCounts
    const serieCode = serie?.code
    const modalId = 'cq-lots'
    let options: any[] = []
    for (let i = 0; i < lots!.length; i++) {
      const currentLotCount = { tested: lotCounts?.find((itmInner) => itmInner.lotId === lots![i].id)?.tested }
      options.push({
        ...lots![i],
        ...currentLotCount,
      })
    }
    options = options.sort((a, b) => (a.code > b.code ? 1 : -1))

    return QualityProviderNew.showLotsModal(modalId, options, serieCode)
  }

  static renderScartoModal = (
    setState: (showScartoModal: boolean) => void,
    scartoItem: (reversible?: boolean) => Promise<void>
  ) => {
    return QualityProviderNew.renderScartoModal(setState, scartoItem)
  }

  static showErrorToast = (res: any) => {
    for (let i = 1; i <= res.errors.length; i++) {
      if (res.errors[i] && res.errors[i].errorCode) {
        //If there is more than one error, show them all in modals, than throw the first of them as effective error
        showToast({
          title: __(T.error.error),
          description: T.error[res.errors[i].errorCode.replaceAll('.', '_')]
            ? __(T.error[res.errors[i].errorCode.replaceAll('.', '_')])
            : res.errors[i].errorCode.replaceAll('.', '_'),
          status: 'error',
        })
      }
    }
  }
}

export const TagReadBox = styled.div<{ bgColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  ${({ bgColor }) => `background-color: ${bgColor}`};
  margin: 20px;
  border-radius: 20px;
`

export const TagReadPlaceholder = styled.div<{ color: string }>`
  font-style: normal;
  font-weight: 700;
  font-size: 38px;
  line-height: 38px;
  text-align: center;
  cursor: default;
  ${({ color }) => `color: ${color}`};
`

export const Counter = styled(Box)`
  min-width: 80px;
  font-size: 20px;
  font-weight: bold;
`

export const CounterContainer = styled(Box)`
  justify-content: space-between;
`
